import { Injectable, inject, PLATFORM_ID, signal } from '@angular/core';
import { PrimeNG } from 'primeng/config';
import { usePreset, updatePreset } from '@primeng/themes';
import { isPlatformBrowser } from '@angular/common';
import { BFGTheme } from '../../theme/bfgtheme';
import { CommunityTheme } from '../../theme/communitytheme';
import { colord } from 'colord';
import { ApiService } from './api.service';

export type AppPresetName = 'bfg' | 'community';

@Injectable({ providedIn: 'root' })
export class ThemeService {
  private primeng = inject(PrimeNG);
  private platformId = inject(PLATFORM_ID);
  private apiService = inject(ApiService);

  isCustomizedThemeSignal = signal(false);

  setPreset(name: 'bfg' | 'community') {
    const preset = name === 'community' ? CommunityTheme : BFGTheme;

    try {
      usePreset(preset);
    } catch {
      console.log('ThemeService: falling back to runtime theme setting');
      this.primeng.theme.set({ preset });
    }

    if (isPlatformBrowser(this.platformId)) {
      document.body.classList.remove('theme-bfg', 'theme-community');
      document.body.classList.add(`theme-${name}`);
    }

  }

  public customizeAuctionTheme(auctionId: number) {
    if (auctionId > 0) {
      this.apiService.getAuctionConfig(auctionId).subscribe(response => {
        this.customizeTheme(
          response.theme.darkColor,
          response.theme.lightColor,
          response.theme.darkColorFontColor,
          response.theme.lightColorFontColor
        );
      });
    }
  }

  public customizeTheme(darkColor: string, lightColor: string, darkFontColor:string, lightFontColor:string) {
    const primaryShades = this.getShades(lightColor);
    const secondaryShades = this.getShades(darkColor);
    usePreset(BFGTheme);
    const props = {
      primitive: {
        sky: secondaryShades,
      },
      semantic: {
          primary: primaryShades,
          colorScheme: {
            light: {
              primary: {
                contrastColor: lightFontColor,
              }
            }
          },
          info: secondaryShades,
          textColorSecondary: darkFontColor,
          textColorPrimary: lightFontColor,
    }}
    updatePreset(props)
  }

  private getShades(hex:string) {
      let shades = {
        50: colord(hex).lighten(0.36).toHex(),
        100: colord(hex).lighten(0.32).toHex(),
        200: colord(hex).lighten(0.24).toHex(),
        300: colord(hex).lighten(0.16).toHex(),
        400: colord(hex).lighten(0.08).toHex(),
        500: hex,
        600: colord(hex).darken(0.08).toHex(),
        700: colord(hex).darken(0.16).toHex(),
        800: colord(hex).darken(0.24).toHex(),
        900: colord(hex).darken(0.32).toHex(),
        950: colord(hex).darken(0.36).toHex(),
      };
      return shades;
  }

  public calculateIsCustomizedTheme(auctionId: number){
    if (auctionId > 0) {
      this.apiService.getAuctionConfig(auctionId).subscribe(response => {
        let isCustomizedLightColor: boolean = (response.theme.lightColor != '#0079BF');
        let isCustomizedDarkColor: boolean = (response.theme.darkColor != '#004973');
        if(isCustomizedLightColor || isCustomizedDarkColor){
          this.isCustomizedThemeSignal.set(true);
        }
      });
    }
  }
}
